import { useState, useEffect, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Home } from 'lucide-react';
import { useAuth } from './AuthContext';
import { AuthService } from './service/Auth.service';
import { AuthFormData } from './dto/Auth.dto';
import { environment } from '../../shared/enviroment/environment';
import './styles/AuthMobile.css';


const authService = new AuthService(environment.api.apiRootServer);

const ErrorMessage = memo(({ error }: { error: string | null }) => (
  error ? (
    <div className="error-message">
      <AlertCircle />
      <span>{error}</span>
    </div>
  ) : null
));

const Spinner = memo(({ text }: { text: string }) => (
  <span>
    <div className="loading-spinner"></div>
    {text}
  </span>
));

const OtpInputs = memo(({ value, onChange, disabled }: { value: string, onChange: (idx: number, val: string) => void, disabled: boolean }) => (
  <div className="otp-container">
    {[0, 1, 2, 3].map((index) => (
      <input
        key={index}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={1}
        className="otp-input"
        onChange={e => onChange(index, e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Backspace' && !value[index] && index > 0) {
            const prevInput = document.querySelector(`input[data-index="${index - 1}"]`) as HTMLInputElement;
            prevInput?.focus();
          }
        }}
        data-index={index}
        value={value[index] || ''}
        disabled={disabled}
        autoComplete="one-time-code"
      />
    ))}
  </div>
));

const Terms = memo(() => (
  <p className="terms-text">
    By continuing, you agree to our{' '}
    <span className="terms-link" onClick={() => window.open('/terms', '_blank')}>Terms of Service</span>
    {' '}and{' '}
    <span className="terms-link" onClick={() => window.open('/privacy', '_blank')}>Privacy Policy</span>
  </p>
));


export default function AuthMobile() {
  const [formData, setFormData] = useState<AuthFormData>({ phone: '', otp: '', name: '' });
  const [localError, setLocalError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      const fromCheckout = sessionStorage.getItem('fromCheckout') === 'true' || new URLSearchParams(location.search).get('fromCheckout') === 'true';
      const fromCart = sessionStorage.getItem('fromCart') === 'true';
      
      let redirectPath = '/';
      if (fromCheckout) {
        sessionStorage.removeItem('fromCheckout');
        redirectPath = '/checkout';
      } else if (fromCart) {
        sessionStorage.removeItem('fromCart');
        redirectPath = '/cart';
      }
      
      // Replace current history entry to prevent going back to auth page
      window.history.replaceState({}, document.title, redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location.search]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [resendCooldown]);



  if (authLoading) {
    return (
      <div className="page-loading">
        <div className="page-loading-spinner" />
      </div>
    );
  }
  if (isAuthenticated) return null;

  // Phone number cleaning and validation
  const cleanPhoneNumber = (phone: string) => {
    if (!phone) return '';
    let cleaned = phone.toString().trim().replace(/[\s\-\(\)\.]/g, '');
    cleaned = cleaned.replace(/^\+/, '');
    // Keep only non-digit removal, preserve country code if present
    if (cleaned.length === 10) {
      return '+91' + cleaned;
    } else if (cleaned.length === 12 && cleaned.startsWith('91')) {
      return '+' + cleaned;
    } else if (cleaned.length > 10) {
      return '+' + cleaned;
    }
    return cleaned;
  };
  
  const validatePhoneNumber = (phone: string) => phone.replace(/\D/g, '').length >= 10;


  const handleSendOtp = async () => {
    setLocalError(null);
    setIsLoading(true);
    
    // Validate phone number
    if (!formData.phone) {
      setIsLoading(false);
      return setLocalError('Please enter your phone number');
    }
    
    const cleanPhone = cleanPhoneNumber(formData.phone);
    if (!validatePhoneNumber(cleanPhone)) {
      setIsLoading(false);
      return setLocalError('Please enter a valid 10-digit phone number');
    }
    
    try {
      console.log('Sending OTP to:', cleanPhone);
      const response = await authService.sendUnifiedOtp({ phone: cleanPhone });
      
      if (response.success) {
        setFormData(prev => ({ ...prev, phone: cleanPhone }));
        console.log('OTP sent successfully');
        setOtpSent(true);
        setResendCooldown(30);
      } else {
        setLocalError(response.message || 'Failed to send OTP');
      }
    } catch (error: any) {
      console.error('OTP send failed:', error);
      setLocalError(error.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLocalError(null);
    setIsLoading(true);
    if (!formData.otp || formData.otp.length !== 4) {
      setIsLoading(false);
      return setLocalError('Please enter a valid 4-digit OTP');
    }
    
    try {
      console.log('Verifying OTP...');
      const response = await authService.verifyUnifiedOtp({
        phone: formData.phone,
        otp: formData.otp,
      });
      
      if (response.success && 'token' in response && 'user' in response) {
        login(response.token!, response.user!);
        console.log('Login successful');
      } else {
        setLocalError(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      setLocalError(error.message || 'Failed to verify OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setLocalError(null); 
    setIsLoading(true);
    
    try {
      console.log('Resending OTP to:', formData.phone);
      const response = await authService.sendUnifiedOtp({ phone: formData.phone });
      
      if (response.success) {
        console.log('OTP resent successfully');
        setResendCooldown(30);
      } else {
        setLocalError(response.message || 'Failed to resend OTP');
      }
    } catch (error: any) {
      console.error('OTP resend error:', error);
      setLocalError(error.message || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = formData.otp.split('');
    newOtp[index] = value;
    setFormData(prev => ({ ...prev, otp: newOtp.join('') }));
    if (value && index < 3) {
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`) as HTMLInputElement;
      nextInput?.focus();
    }
  };

  return (
    <div className="auth-container-mobile">
      <button 
        onClick={() => navigate('/')}
        className="home-button"
        aria-label="Go to home"
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#f8fafc';
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = '#fff';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <Home size={20} color="#4b5563" />
      </button>
      <main className="auth-main-mobile">
        <div className="auth-card-mobile auth-grid-mobile">
          <aside className="auth-left-mobile auth-left-image-mobile">
          </aside>
          <section className="auth-right-mobile">
            <div className="auth-right-header-mobile">
              <h2 className="auth-title-mobilee">
                {otpSent ? 'Verify Your Phone' : 'Login To Your Account'}
              </h2>
            </div>
            <div className="auth-form-mobile">
            {!otpSent ? (
              <>
                <div className="form-group-mobile-mobile">
                  <label htmlFor="phone" className="form-label-mobile">Phone Number</label>
                  <div className="phone-input-container-mobile">
                    <div className="country-code-mobile"><span>+91</span></div>
                    <input 
                      type="tel" 
                      id="phone" 
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={formData.phone} 
                      onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))} 
                      onKeyPress={e => {
                        if (e.key === 'Enter' && !isLoading) {
                          handleSendOtp();
                        }
                      }}
                      className="phone-input" 
                      placeholder="Enter your phone number" 
                      disabled={isLoading}
                      autoComplete="tel"
                    />
                  </div>
                </div>
                <ErrorMessage error={localError} />
                <button 
                  onClick={handleSendOtp} 
                  disabled={isLoading} 
                  className="btn btn-primary"
                >
                  {isLoading ? <Spinner text="Sending OTP..." /> : 'Continue'}
                </button>
              </>
            ) : (
              <>
                <div className="form-group-mobile">
                  <label className="form-label-verify">Enter verification code</label>
                  <OtpInputs 
                    value={formData.otp} 
                    onChange={handleOtpChange} 
                    disabled={isLoading} 
                  />
                </div>
                <ErrorMessage error={localError} />
                <div className="form-group-resend-mobile">
                  <button 
                    onClick={handleResendOtp} 
                    disabled={resendCooldown > 0 || isLoading} 
                    className="resend-button"
                  >
                    {resendCooldown > 0 
                      ? `Resend code in ${resendCooldown}s` 
                      : 'Resend code'}
                  </button>
                </div>
                <div className="form-group-verify-mobile">
                  <button 
                    onClick={handleVerifyOtp} 
                    disabled={isLoading || formData.otp.length !== 4} 
                    className="btn btn-primary"
                  >
                    {isLoading ? <Spinner text="Verifying..." /> : 'Verify OTP'}
                  </button>
                </div>
              </>
            )}
            <Terms />
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}